import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, BookOpen, Mic, Globe } from "lucide-react";
import { profile, aboutIntro, stats, books, writeups } from "../mock";
import useScrollReveal from "../hooks/useScrollReveal";

export default function Home() {
  useScrollReveal();

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-[#1a2b4a] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1727221167365-d365e63e7ebc"
            alt="United Nations"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#141f38] via-[#1a2b4a]/90 to-[#1a2b4a]/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full grid lg:grid-cols-12 gap-12 items-center pt-32 pb-20">
          <div className="lg:col-span-7 animate-fade-up">
            <p className="text-xs uppercase tracking-[0.35em] text-[#e6c98d] mb-6">
              {profile.tagline}
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-white leading-[1.02]">
              T.P.
              <br />
              Sreenivasan
            </h1>
            <div className="w-20 h-[3px] bg-[#b8935a] my-8" />
            <p className="max-w-xl text-lg md:text-xl text-white/75 leading-relaxed font-light">
              {profile.title}. Thirty-seven years in the Indian Foreign Service,
              two decades of multilateral diplomacy, and a distinguished second
              innings as author, columnist and educator.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/about"
                className="group inline-flex items-center gap-2 bg-[#b8935a] hover:bg-[#a67f45] text-white px-8 py-4 rounded-md font-medium tracking-wide transition-colors duration-300"
              >
                Read Biography
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform duration-300"
                />
              </Link>
              <Link
                to="/books"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-8 py-4 rounded-md font-medium tracking-wide transition-colors duration-300"
              >
                Explore Books
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 animate-scale-in">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-4 border border-[#b8935a]/40 rounded-lg translate-x-4 translate-y-4" />
              <img
                src={profile.portrait}
                alt="T.P. Sreenivasan"
                className="relative w-full aspect-[4/5] object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO / ABOUT PREVIEW */}
      <section className="py-24 bg-[#faf8f4]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center reveal">
          <Quote className="mx-auto text-[#b8935a] mb-6" size={40} />
          <p className="font-serif text-2xl md:text-3xl leading-relaxed text-[#1a2b4a] italic">
            "{aboutIntro}"
          </p>
          <Link
            to="/about"
            className="group mt-10 inline-flex items-center gap-2 text-[#b8935a] font-medium tracking-wide hover:gap-3 transition-all duration-300"
          >
            Full biography
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-[#1a2b4a]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center reveal" style={{ transitionDelay: `${i * 90}ms` }}>
              <div className="font-serif text-5xl md:text-6xl font-bold text-[#e6c98d]">
                {s.value}
              </div>
              <div className="mt-3 text-sm text-white/70 leading-snug px-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-24 bg-[#faf8f4]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Globe, title: "Diplomacy", text: "Ambassador to the UN in Vienna & New York, Kenya, Fiji and the South Pacific.", link: "/about" },
              { icon: BookOpen, title: "Authorship", text: "Author of four acclaimed books on diplomacy, mythology and contemporary foreign policy.", link: "/books" },
              { icon: Mic, title: "Public Voice", text: "A sought-after speaker and prolific columnist on world affairs and India's global role.", link: "/writeup" },
            ].map((c, i) => (
              <Link
                key={i}
                to={c.link}
                className="group bg-white rounded-lg p-8 border border-[#eae4d8] hover:border-[#b8935a] hover:-translate-y-1 transition-all duration-300 reveal"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <div className="w-14 h-14 rounded-full bg-[#1a2b4a] flex items-center justify-center text-[#e6c98d] group-hover:bg-[#b8935a] group-hover:text-white transition-colors duration-300">
                  <c.icon size={24} />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#1a2b4a] mt-6">
                  {c.title}
                </h3>
                <p className="mt-3 text-[#5a6070] leading-relaxed">{c.text}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#b8935a]">
                  Learn more
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BOOK + WRITING */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div className="reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-[#b8935a] mb-3">Featured Work</p>
            <h2 className="font-serif text-4xl font-bold text-[#1a2b4a] gold-underline">Latest Book</h2>
            <div className="mt-10 flex gap-6">
              <img
                src={books[0].cover}
                alt={books[0].title}
                className="w-40 h-56 object-cover rounded-md shadow-lg flex-shrink-0"
              />
              <div>
                <p className="text-sm text-[#b8935a] font-medium">{books[0].year}</p>
                <h3 className="font-serif text-2xl font-semibold text-[#1a2b4a] mt-1 leading-snug">
                  {books[0].title}
                </h3>
                <p className="mt-3 text-[#5a6070] leading-relaxed text-sm">
                  {books[0].excerpt}
                </p>
                <Link
                  to="/books"
                  className="mt-5 inline-flex items-center gap-2 text-[#b8935a] font-medium text-sm hover:gap-3 transition-all"
                >
                  View all books <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          <div className="reveal" style={{ transitionDelay: "120ms" }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#b8935a] mb-3">From the Desk</p>
            <h2 className="font-serif text-4xl font-bold text-[#1a2b4a] gold-underline">Recent Writing</h2>
            <ul className="mt-10 divide-y divide-[#eae4d8]">
              {writeups.slice(0, 4).map((w) => (
                <li key={w.id} className="py-4 group">
                  <Link to="/writeup" className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-medium text-[#1a2b4a] group-hover:text-[#b8935a] transition-colors">
                        {w.title}
                      </h4>
                      <p className="text-xs text-[#8a8f9c] mt-1">
                        {w.source} · {w.date}
                      </p>
                    </div>
                    <ArrowRight
                      size={18}
                      className="text-[#c9cdd6] group-hover:text-[#b8935a] group-hover:translate-x-1 transition-all mt-1 flex-shrink-0"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
